import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useCallback,
  useRef,
  useState,
} from "react";

export type ReferencedState<T> = {
  /** State value - triggers render */
  value: T;
  /** Always the latest synchronous value */
  current: T;
  /** Ref object (for passing to other hooks) */
  ref: RefObject<T>;
  /** Updates both state and ref */
  set: Dispatch<SetStateAction<T>>;
};

/**
 * A hook that combines useState + useRef for synchronous access without stale closures.
 *
 * - `value` - React state, triggers re-render
 * - `current` - Always the latest value (synchronous read)
 * - `ref` - The ref object itself (for passing to other hooks)
 * - `set` - Updates both state and ref atomically
 */
export function useReferencedState<T>(initialValue: T): ReferencedState<T> {
  const [value, setValue] = useState<T>(initialValue);
  const ref = useRef<T>(initialValue);

  const set: Dispatch<SetStateAction<T>> = useCallback((next) => {
    const resolved =
      typeof next === "function" ? (next as (prev: T) => T)(ref.current) : next;

    ref.current = resolved;
    setValue(resolved);
  }, []);

  return {
    value,
    get current() {
      return ref.current;
    },
    ref,
    set,
  };
}
